import React, { useState, useEffect, useCallback } from 'react';
import { CoachesService } from '../../services/coachesService';
import { Student } from '../../types/student';
import Modal from '../../Common/Components/Modal';

interface ViewStudentsDialogProps {
  coachId: string;
  onClose: () => void;
  show: boolean;
}

const ViewStudentsDialog: React.FC<ViewStudentsDialogProps> = ({ coachId, onClose, show }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadStudents = useCallback(async () => {
    try {
      setIsLoading(true);
      const studentsData = await CoachesService.getStudentsByCoachId(coachId);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setIsLoading(false);
    }
  }, [coachId]);

  useEffect(() => {
    if (show) {
      loadStudents();
    }
  }, [show, loadStudents]);

  return (
    <Modal show={show} onHide={onClose} id="defaultModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Students List</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        ) : students.length === 0 ? (
          <p className="text-center py-8 text-slate-500 dark:text-zink-200">No students found</p>
        ) : (
          <div className="space-y-4">
            {students.map((student) => (
              <div key={student.id} className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-zink-500">
                <div className="size-10 rounded-full bg-green-100 dark:bg-green-500/20 shrink-0">
                  <img
                    src={student.avatarUrl}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) nextElement.style.display = 'flex';
                    }}
                  />
                  <div className="size-10 bg-gray-100 rounded-full dark:bg-gray-500/20 items-center justify-center hidden">
                    <span className="text-gray-500">👤</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{student.fullName}</p>
                  <p className="text-xs text-slate-500 dark:text-zink-200 truncate">{student.email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <div></div>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewStudentsDialog;